import { Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { Search, Shield, Zap, ArrowRight, Star } from 'lucide-react'
import { createSoftwareAppStructuredData, SOURCEDIN_SOFTWARE_DATA } from '@/components/structured-data/software-app'

export function SEOHomepage() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SourcedIn - RFP Marketplace",
    "description": "The leading B2B marketplace connecting buyers and sellers through Request for Proposals. Find qualified vendors, submit competitive bids, and grow your business.",
    "url": "https://sourcedin.ai",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://sourcedin.ai/request-for-proposal-marketplace?search={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "sameAs": [
      "https://linkedin.com/company/sourcedin",
      "https://twitter.com/sourcedin"
    ]
  }

  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SourcedIn",
    "description": "Leading RFP marketplace platform for B2B procurement and vendor matching",
    "url": "https://sourcedin.ai",
    "logo": "https://sourcedin.ai/images/logo.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-555-SOURCED",
      "contactType": "customer service"
    }
  }

  // Create SoftwareApplication structured data
  const softwareAppData = createSoftwareAppStructuredData(SOURCEDIN_SOFTWARE_DATA)

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <SEOHead
        title="SourcedIn - Leading RFP Marketplace | B2B Buyer & Seller Matching Platform"
        description="Connect with qualified vendors and buyers on the world's leading RFP marketplace. Streamline your procurement process, find the perfect business partners, and grow your revenue through competitive bidding."
        keywords="RFP marketplace, request for proposal platform, B2B marketplace, vendor matching, procurement platform, business proposals, competitive bidding, supplier sourcing, tender platform"
        canonical="https://sourcedin.ai"
        structuredData={[structuredData, organizationData, softwareAppData]}
      />

      {/* Hero Section */}
      <section className="relative py-20 px-4 text-center">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              The World's Leading
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> RFP Marketplace</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
              Connect buyers and sellers through our intelligent Request for Proposal platform. 
              Streamline procurement, find qualified vendors, and win more business with data-driven matching.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link href="/register">
              <Button size="lg" className="text-lg px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                Start Finding Vendors <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button variant="outline" size="lg" className="text-lg px-8 py-4 border-2 hover:bg-gray-50">
                Browse RFPs <Search className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">10K+</div>
              <div className="text-gray-600">Active RFPs</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">50K+</div>
              <div className="text-gray-600">Registered Vendors</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">$2B+</div>
              <div className="text-gray-600">Projects Matched</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">95%</div>
              <div className="text-gray-600">Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose SourcedIn for Your RFP Process?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our platform revolutionizes how businesses connect, making procurement faster, 
              more transparent, and significantly more effective.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-shadow border-0 shadow-md">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Smart Vendor Matching</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  Our AI-powered algorithm matches your RFPs with the most qualified vendors based on 
                  expertise, location, budget, and past performance ratings.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow border-0 shadow-md">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-2xl">Secure & Compliant</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  Enterprise-grade security with end-to-end encryption, compliance tracking, 
                  and audit trails. Your sensitive procurement data is always protected.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow border-0 shadow-md">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl">Faster Procurement</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  Reduce procurement cycles by up to 70%. Automated workflows, instant notifications, 
                  and streamlined evaluation tools accelerate your entire RFP process.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How Our RFP Marketplace Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, transparent, and effective. Get started in minutes and see results in days.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* For Buyers */}
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-2xl font-bold mb-4">Post Your RFP</h3>
              <p className="text-gray-600 leading-relaxed">
                Create detailed RFPs with requirements, budgets, and timelines. 
                Our platform guides you through best practices for maximum response quality.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-2xl font-bold mb-4">Get Matched</h3>
              <p className="text-gray-600 leading-relaxed">
                Our AI instantly notifies qualified vendors who match your criteria. 
                Watch as competitive proposals flow in from verified suppliers.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-2xl font-bold mb-4">Choose & Connect</h3>
              <p className="text-gray-600 leading-relaxed">
                Compare proposals, communicate directly with vendors, and make informed decisions. 
                Close deals faster with built-in collaboration tools.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Trusted by Industry Leaders
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of successful businesses already using SourcedIn
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-lg text-gray-700 mb-4 italic">
                "SourcedIn transformed our procurement process. We reduced vendor sourcing time by 60% 
                and found higher quality suppliers than ever before."
              </blockquote>
              <div className="font-semibold">Sarah Johnson, CPO at TechCorp</div>
            </Card>

            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-lg text-gray-700 mb-4 italic">
                "As a vendor, SourcedIn has been a game-changer. The quality of RFPs and buyer 
                interactions is exceptional. We've grown our business by 200%."
              </blockquote>
              <div className="font-semibold">Mike Chen, CEO at Design Solutions</div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join the leading RFP marketplace and connect with your ideal business partners today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
                Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button size="lg" variant="outline" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-blue-600">
                Explore RFPs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
